import { APIHostTestParameters, validateTestParameters, detailForFetchResponse, detailForFetchException, detailmsg } from "./controller-request"
import { TestState, TestDisplayer, TestGroup } from "./common"
import { eltbit } from "../visual/domutils"

interface APIHostSubcheck {
    url: string;
    check: () => Promise<boolean>;
    noteStatus: (state: TestState, ...summary: eltbit[]) => void;

    // detailery/ConnectionTest impl
    identifier: string
    status: TestState
    summary: eltbit[]
    details?: eltbit[][]
}


class APIHostCheck {
    identifier: string
    location: string;
    hostname: string;
    queueRerender: () => void
    subtests: Array<APIHostSubcheck>;
    running: boolean
    summary: eltbit[]

    constructor(disp: TestGroup, location: string, hostname: string) {
        this.identifier = location
        this.location = location
        this.hostname = hostname
        this.queueRerender = disp.queueRerender
        this.subtests = []
        this.running = true

        this.summary = [ "Checking if we can communicate with servers at ", this.location, "…" ]
    }

    updateParameters(sinfo: APIHostTestParameters) {
        this.running = false

        console.log("Updating test parameters for", this.location, sinfo)

        if (!validateTestParameters(sinfo)) {
            this.summary = [ "Unknown test parameters" ]
            this.queueRerender()
            return
        }

        function mkurl(scheme: string, path: string): string {
            let where = new URL('https://example.com/')
            where.protocol = scheme;
            where.hostname = sinfo.host;
            where.pathname = path;
            return where.href
        }

        if (sinfo.stpath !== undefined) {
            let url = mkurl('https:', sinfo.stpath)
            this.includeTest(url, 'Checking HTTPS…',
                             () => new SimpleHTTPSGet(this, url, sinfo.host))
        }

        if (sinfo.wspath !== undefined) {
            let url = mkurl('wss:', sinfo.wspath)
            this.includeTest(url, 'Checking WebSocket…',
                             () => new WebSocketTest(this, url))
        }
    }

    includeTest(url: string, description: string, mk: () => APIHostSubcheck) {
        if (this.subtests.some( t => t.url === url )) {
            return
        }
        let test = mk()
        this.subtests.push(test)
        test.check().catch(
            (nok: any) => {
                console.log(nok)
                test.noteStatus(TestState.Fatal, "Test failed.")
                return false
            }
        )
        this.queueRerender()
    }
}

class SimpleHTTPSGet extends TestDisplayer {

    url: string;
    expected: string | null;

    constructor(disp: APIHostCheck, url: string, expected: string | null) {
        super(disp, url)
        this.url = url
        this.expected = expected
    }

    async check(): Promise<boolean> {
        var success : boolean
        var contents : string
        let url = this.url
        let expected_contents = this.expected

        try {
            let res = await fetch(url, { redirect: 'error' })
            success = res.ok || (expected_contents === null && res.status < 400)
            if (!success) {
                this.noteDetail(...detailForFetchResponse(res, url));
            }
            contents = await res.text();
        } catch(err) {
            success = false;
            contents = "";
            this.noteDetail(...detailForFetchException(err, url));
        }

        if (!success) {
            this.noteStatus(TestState.Fatal, 'HTTPS failed.');
            return false;
        }

        if (expected_contents !== null) {
            let posn = contents.indexOf(expected_contents);
            if (posn !== 0 || (contents.slice(posn + expected_contents.length).trim() !== "")) {
                this.noteStatus(TestState.Fatal, 'Garbled response.');
                console.log('Response from', url, 'was', contents);
                return false;
            }
        }

        this.noteStatus(TestState.Ok, 'HTTPS succeeded.');
        return success;
    }

}

class WebSocketTest extends TestDisplayer {

    url: string;

    constructor(disp: APIHostCheck, url: string) {
        super(disp, url)
        this.url = url
    }

    check(): Promise<boolean> {

        let url = this.url
        let displayer = this

        return new Promise<boolean>( (resolve, reject) => {

            var ctxt = {
                wss: new WebSocket(url, 'v1.test.omnifocus.com'),

                opened: false,
                failed: false,
                gotHello: false,
                expectedResponse: null as (string | null),
                gotResponse: false,
                timer: null as (number | null),

                timedOut() {

                    if (this.gotHello && (this.expectedResponse === null)) {
                        let msg = "Hi there" + ".".repeat(10 + Math.floor(10 * Math.random()));
                        this.expectedResponse = ( String(msg.length) + " " )
                        this.wss.send(msg)
                        this.setTimer(3.0)
                        return
                    }

                    this.failed = true;
                    console.log(url, ": test timed out")
                    this.wss.close()
                    resolve(false)
                },

                setTimer(delay: number | null) {
                    if (this.timer !== null) {
                        window.clearTimeout(this.timer)
                        this.timer = null
                    }

                    console.log("resetting the timer to ", delay)
                    if (delay !== null) {
                        this.timer = window.setTimeout(() => { this.timedOut() }, delay * 1000)
                    }
                },

                didOpen(evt: Event) {
                    console.log(evt);
                    if (this.opened) {
                        this.failed = true;
                    } else {
                        this.opened = true;
                    }
                    this.setTimer(10)
                    console.log(url, ": readyState", this.wss.readyState);
                },

                didClose(evt: CloseEvent) {
                    console.log(evt);
                    this.setTimer(null)
                    if (!evt.wasClean) {
                        this.failed = true;
                    }
                    if ((evt.code !== 1006 && evt.code !== 1000) || evt.reason !== "") {
                        displayer.noteDetail({
                            'elt': 'p', value: [ "Websocket closed with code ", String(evt.code), ": ", String(evt.reason), "." ]
                        } )
                    }
                    resolve(this.opened && !this.failed);
                },

                gotMessage(msgData: string) {
                    // We expect the string "hello", then a response to our message, then a connection close.
                    if (!this.gotHello) {
                        if (msgData !== "hello") {
                            this.failed = true;
                        } else {
                            this.gotHello = true;
                        }
                        this.setTimer(0.25)
                    } else if (this.expectedResponse === null) {
                        this.failed = true;
                    } else if (!this.gotResponse) {
                        if (!msgData.startsWith(this.expectedResponse)) {
                            this.failed = true;
                        }
                        this.gotResponse = true;
                        this.setTimer(1.0)
                    } else {
                        this.failed = true;
                    }
                }
            }

            ctxt.wss.addEventListener('open', (evt) => { ctxt.didOpen(evt) })
            ctxt.wss.addEventListener('close', (evt) => { ctxt.didClose(evt) })

            ctxt.wss.addEventListener('error', (evt) => {
                console.log(evt);
                ctxt.failed = true;
                ctxt.setTimer(1)  // We expect a close event hard on the heels of this one
                displayer.noteDetail(
                    { 'elt': 'p',
                      value: "The browser reported a WebSocket error (no details available)."
                    }
                );
                if (ctxt.wss.readyState === ctxt.wss.OPEN || ctxt.wss.readyState === ctxt.wss.CONNECTING) {
                    ctxt.wss.close();
                }
            });

            ctxt.wss.addEventListener('message', (evt) => {
                console.log(url, ": ", evt);
                ctxt.gotMessage(evt.data)
            })

        }).then(
            (success) => {
                if (success) {
                    displayer.noteStatus(TestState.Ok, 'WebSocket succeeded.');
                } else {
                    displayer.noteStatus(TestState.Fatal, 'WebSocket failed.');
                    displayer.noteDetail(detailmsg(url))
                }
                return success;
            }
        )
    }
}

export { APIHostCheck }
